<template>
	<Modal v-model:visible="visible" :title="$t('settings.vrsSettingsForm.pppRtk.cnetImportConfirmModal.header')" :width="900" @ok="onOk" @cancel="onCancel">
		<Table :columns="columns" :data-source="tableRecords" :pagination="false" size="small">
			<template #bodyCell="{ column, record }">
				<template v-if="column.dataIndex === 'cn_num'">
					<Select
						v-if="['confirm', 'add'].includes(record.status)"
						class="ui-select"
						v-model:value="record.cn_num"
						:options="cnetNumberOptions"
						:loading="isLoadingCNetList"
						:placeholder="$t('settings.vrsSettingsForm.pppRtk.cnetUpdateForm.cnetNumberPlaceholder')"
					/>
					<span v-else>{{ record.cn_num != null ? 'C-Net ' + record.cn_num : '—' }}</span>
				</template>
				<template v-else-if="column.dataIndex === 'status'">
					<LabeledSwitch
						v-if="['confirm', 'add'].includes(record.status)"
						v-model="record.store"
						:small="true"
						:allowWordWrap="false"
						:rightLabel="$t('confirmImportModal.statuses.' + record.status)"
					/>
					<template v-else-if="record.status === 'error'">
						<TypographyText type="danger">{{ $t('confirmImportModal.statuses.' + record.status) }}</TypographyText>
					</template>
					<template v-else>
						{{ $t('confirmImportModal.statuses.' + record.status) }}
					</template>
				</template>
				<template v-else-if="column.dataIndex === 'message'">
					<TypographyText v-if="record.status === 'error'" type="danger">{{ record.messageText }}</TypographyText>
					<template v-else>{{ record.messageText }}</template>
				</template>
			</template>
		</Table>
		<template #footer>
			<Button key="back" @click="onCancel">
				{{ $t('settings.vrsSettingsForm.pppRtk.cnetImportConfirmModal.cancelButtonLabel') }}
			</Button>
			<Button key="submit" type="primary" :loading="isSubmitting" :disabled="isImportDisabled" @click="onOk">
				{{ $t('settings.vrsSettingsForm.pppRtk.cnetImportConfirmModal.importButtonLabel') }}
			</Button>
		</template>
	</Modal>
</template>

<script>
import { ref, computed, watch } from 'vue';
import { useVModel } from '@vueuse/core';

import { Modal, Button, Table, Select, TypographyText, message } from 'ant-design-vue';

import { useI18n } from 'vue-i18n';

import Api from '@/api/Api';
import Consts from '@/api/Consts';

import LabeledSwitch from '@/components/common/LabeledSwitch.vue';

import { useCNetNumberSelectData } from '@/components/common/controls/useCNetNumberSelectData';

const prepareRecord = item => ({
	...item,
	week: item.obj_data.week != null ? item.obj_data.week : '—',
	cn_num: item.message?.cn_num != null ? null : item.obj_data.cn_num,
	gridpoint_count: item.obj_data.gridpoints.length,
	messageText: item.message != null ? Object.values(item.message)[0] : null,
	store: item.status === 'add' || item.status === 'confirm',
});

export default {
	components: {
		Modal, Button, Table, Select, LabeledSwitch, TypographyText,
	},
	props: {
		modelValue: {
			type: Boolean,
			default: false,
		},
		subNetworkId: {
			type: Number,
			required: true,
		},
		importData: {
			type: Object,
			default: () => ({}),
		},
		onSuccess: {
			type: Function,
			required: true,
		},
	},
	emits: ['update:modelValue'],
	setup(props, { emit }) {
		const { t } = useI18n();
		const isSubmitting = ref(false);
		const tableRecords = ref([]);

		const visible = useVModel(props, 'modelValue', emit);

		const selectedCheckedRecords = computed(() => tableRecords.value.filter(record => record.store));
		const hasDuplicateCnetNumbers = computed(() => {
			const numbers = selectedCheckedRecords.value.map(record => record.cn_num).filter(value => value != null);
			return numbers.length !== new Set(numbers).size;
		});
		const hasEmptyCnetNumbers = computed(() => selectedCheckedRecords.value.filter(record => record.cn_num == null).length > 0);
		const isImportDisabled = computed(() => (
			selectedCheckedRecords.value.length === 0 || hasDuplicateCnetNumbers.value || hasEmptyCnetNumbers.value
		));

		const { isLoadingCNetList, cnetNumberOptions, loadCNetNumbers } = useCNetNumberSelectData(ref(null));

		watch(visible, value => {
			if (value) {
				loadCNetNumbers();
			}
		});

		watch(() => props.importData, value => {
			const items = value.items != null ? value.items : [];
			tableRecords.value = items.map(prepareRecord);
		}, { immediate: true });

		const onOk = () => {
			isSubmitting.value = true;
			const toImport = selectedCheckedRecords.value.map(record => ({
				...record.obj_data,
				cn_num: record.cn_num,
			}));
			Api.subNetworkCnetJsonUploadFinish(props.subNetworkId, toImport, response => {
				if (response.status === 'ok') {
					message.success(t('settings.vrsSettingsForm.pppRtk.cnetImportConfirmModal.messages.success'));
					props.onSuccess();
				} else {
					message.error(t('settings.vrsSettingsForm.pppRtk.cnetImportConfirmModal.messages.error'));
				}
				isSubmitting.value = false;
				visible.value = false;
			});
		};

		const onCancel = () => {
			visible.value = false;
		};

		return {
			isSubmitting,
			tableRecords,
			visible,
			isImportDisabled,
			isLoadingCNetList,
			cnetNumberOptions,
			onOk,
			onCancel,
			columns: Consts.cnetImportConfirmTableColumnsComp,
		};
	},
};
</script>

<style scoped>
.ui-select {
	min-width: 150px;
}
</style>
